import {
  ClaimsApi,
  Configuration,
  DocumentsApi,
  FetchError,
  ResponseError,
  SettingsApi,
  type ClaimPresentPositions,
  type ClaimResponse,
  type DocumentInfo,
  type UpdatePresentPositionRequest,
  type UpdatePresentPositionResponse,
  type Participant,
  type ParticipantsResponse,
  type AdhocContact,
  type AdhocContactsResponse,
  type AddAdhocContactRequest,
  type AddAdhocContactResponse,
  type CreateCaseNoteRequest,
  type CreateCaseNoteResponse,
} from '@api-sdk'
import { ClaimSearchResult } from '../types/claim'
import type { Contact } from '../types/contact'

// Configuration constants
const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_BASE_URL || 'https://localhost:7001',
} as const

// Error messages
const ERROR_MESSAGES = {
  UNAUTHORIZED: 'Unauthorized: Please re-authenticate.',
  BAD_REQUEST: 'Bad request: Please provide a search query.',
  SEARCH_FAILED: (status: number) => `Search failed with status ${status}`,
  REQUEST_FAILED: (status: number) => `Request failed with status ${status}`,
} as const

// HTTP status codes
const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  BAD_REQUEST: 400,
} as const

// 60s: some MC1-backed calls (claim details, participants) can be slow, so allow
// headroom before aborting rather than surfacing a premature timeout error.
const TIMEOUT_MS = 60000

export class ApiUnauthorizedError extends Error {
  constructor(message: string = ERROR_MESSAGES.UNAUTHORIZED) {
    super(message)
    this.name = 'ApiUnauthorizedError'
  }
}

export class ApiTimeoutError extends Error {
  constructor(message: string = 'Request timed out. Please check your connection and try again.') {
    super(message)
    this.name = 'ApiTimeoutError'
  }
}

export class ApiNetworkError extends Error {
  constructor(message: string = 'Network error. Please check your connection and try again.') {
    super(message)
    this.name = 'ApiNetworkError'
  }
}

// A business rule enforced by an MC1 stored proc rejected the request (HTTP 400). Unlike a
// transport failure, retrying the same values can never succeed, so callers surface the
// proc's own reason rather than a generic "please try again".
export class ApiValidationError extends Error {
  readonly messages: string[]
  constructor(messages: string[]) {
    super(messages.join(' '))
    this.name = 'ApiValidationError'
    this.messages = messages
  }
}

// The procs report rule failures as { errors: [{ code, message }] } with messages like
// "[!] Msg 105 - Adhoc Contact / Adhoc Email combo already exists". The shape isn't in the
// OpenAPI spec, so it's parsed defensively — an unexpected body yields no messages and the
// caller falls back to generic handling.
interface ValidationErrorBody {
  errors?: { code?: string; message?: string }[]
}

// Drops the proc's "[!] Msg 105 - " prefix, which is diagnostic noise to the user.
const PROC_MESSAGE_PREFIX = /^\s*\[!]\s*Msg\s*\d+\s*-\s*/i

async function readValidationMessages(response: Response): Promise<string[]> {
  try {
    const body = (await response.json()) as ValidationErrorBody
    return (body.errors ?? []).map((e) => (e.message ?? '').replace(PROC_MESSAGE_PREFIX, '').trim()).filter(Boolean)
  } catch {
    // Not JSON, or the body was already consumed — fall back to generic handling.
    return []
  }
}

/**
 * Creates an AbortSignal that times out after TIMEOUT_MS.
 * Merges with an optional external signal.
 */
function createTimeoutSignal(externalSignal?: AbortSignal): AbortSignal {
  const signals: AbortSignal[] = [AbortSignal.timeout(TIMEOUT_MS)]
  if (externalSignal) signals.push(externalSignal)
  return AbortSignal.any(signals)
}

/**
 * Create API configuration with access token
 */
function createApiConfig(token: string): Configuration {
  const headers: Record<string, string> = {}
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return new Configuration({
    basePath: API_CONFIG.BASE_URL,
    headers,
  })
}

function handleResponseError(error: ResponseError): never {
  const status = error.response.status
  if (status === HTTP_STATUS.UNAUTHORIZED) {
    throw new ApiUnauthorizedError()
  }
  if (status === HTTP_STATUS.BAD_REQUEST) {
    throw new Error(ERROR_MESSAGES.BAD_REQUEST)
  }
  throw new Error(ERROR_MESSAGES.SEARCH_FAILED(status))
}

function handleError(error: unknown): never {
  if (error instanceof ResponseError) {
    return handleResponseError(error)
  }
  if (error instanceof DOMException && error.name === 'TimeoutError') {
    throw new ApiTimeoutError()
  }
  if (error instanceof FetchError) {
    throw new ApiNetworkError()
  }
  throw error
}

/**
 * Search claims by query string
 */
export async function searchClaims(
  query: string,
  searchType: string,
  token: string,
  signal?: AbortSignal
): Promise<ClaimSearchResult[]> {
  const config = createApiConfig(token)
  const api = new ClaimsApi(config)

  try {
    const fullQuery = searchType ? `${searchType}:${query}` : query
    const timeoutSignal = createTimeoutSignal(signal)
    const response = await api.searchClaimsRaw({ query: fullQuery }, { signal: timeoutSignal })
    const data = (await response.raw.json()) as ClaimSearchResult[]
    return data.map((claim) => ({
      ...claim,
      claimTitle: claim.claimTitle || claim.lossLocation || claim.insured || claim.claimNumber,
    }))
  } catch (error) {
    handleError(error)
  }
}

interface RecentClaimResponse {
  claimNumber: string
  primaryClient: string | null
  insured: string | null
  broker: string | null
  dateOfLoss: string
  lastAccessedAt: string
  lossLocation: string | null
}

function mapRecentClaim(claim: RecentClaimResponse): ClaimSearchResult {
  return {
    claimNumber: claim.claimNumber,
    claimTitle: claim.lossLocation || claim.insured || claim.claimNumber,
    insured: claim.insured || '',
    primaryClient: claim.primaryClient || '',
    policyType: '',
    presentPosition: '',
    dateOfLoss: claim.dateOfLoss,
    broker: claim.broker || '',
    totalReserves: 0,
    currency: '',
    lossLocation: null,
  }
}

/**
 * Get recent claims
 */
export async function recentClaims(token: string): Promise<ClaimSearchResult[]> {
  const config = createApiConfig(token)
  const api = new ClaimsApi(config)

  try {
    const timeoutSignal = createTimeoutSignal()
    const response = await api.getRecentClaimsRaw({ signal: timeoutSignal })
    const data = (await response.raw.json()) as RecentClaimResponse[]
    return data.map(mapRecentClaim)
  } catch (error) {
    handleError(error)
  }
}

function createApi(token: string) {
  return new ClaimsApi(createApiConfig(token))
}

function createDocumentsApi(token: string) {
  return new DocumentsApi(createApiConfig(token))
}

function createSettingsApi(token: string) {
  return new SettingsApi(createApiConfig(token))
}

async function apiCall<T>(fn: () => Promise<{ raw: Response }>): Promise<T> {
  try {
    const response = await fn()
    return (await response.raw.json()) as T
  } catch (error) {
    return handleError(error)
  }
}

export async function getClaim(claimNumber: string, token: string): Promise<ClaimResponse> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  return apiCall<ClaimResponse>(() => api.getClaimRaw({ claimNumber }, { signal }))
}

export async function tryGetClaim(claimNumber: string, token: string): Promise<ClaimResponse | null> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  try {
    const response = await api.getClaimRaw({ claimNumber }, { signal })
    return (await response.raw.json()) as ClaimResponse
  } catch (error) {
    if (error instanceof ResponseError && error.response.status === 404) {
      return null
    }
    return handleError(error)
  }
}

export async function getClaimFolderId(claimNumber: string, token: string): Promise<string | null> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  try {
    const response = await api.getClaimFolderIdRaw({ claimNumber }, { signal })
    return await response.value()
  } catch (error) {
    if (error instanceof ResponseError && error.response.status === 404) {
      return null
    }
    return handleError(error)
  }
}

export async function getClaimDocuments(folderId: string, token: string): Promise<DocumentInfo[]> {
  const api = createDocumentsApi(token)
  const signal = createTimeoutSignal()
  return apiCall<DocumentInfo[]>(() => api.getDocumentsRaw({ folderId }, { signal }))
}

export async function uploadDocumentToFolder(
  folderId: string,
  file: File,
  token: string,
  cancelSignal?: AbortSignal
): Promise<DocumentInfo> {
  const api = createDocumentsApi(token)
  const signal = createTimeoutSignal(cancelSignal)
  return apiCall<DocumentInfo>(() => api.uploadDocumentRaw({ folderId, file }, { signal }))
}

export async function getPresentPositionOptions(claimNumber: string, token: string): Promise<ClaimPresentPositions[]> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  return apiCall<ClaimPresentPositions[]>(() => api.getClaimPresentPositionsRaw({ claimNumber }, { signal }))
}

export async function updatePresentPosition(
  claimNumber: string,
  request: UpdatePresentPositionRequest,
  token: string
): Promise<UpdatePresentPositionResponse> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  return apiCall<UpdatePresentPositionResponse>(() =>
    api.updateClaimPresentPositionRaw({ claimNumber, updatePresentPositionRequest: request }, { signal })
  )
}

// ─── Participants & ad-hoc contacts (Phase 2, Epic 2.6 / 2.7) ──────────────────
// Both come from real read endpoints:
//   GET /api/claims/{claimNumber}/participants
//   GET /api/claims/{claimNumber}/adhoc-contacts
// Neither SDK type carries an id, so a stable React key is derived from email,
// falling back to the list position. Unmatched email recipients are surfaced as
// ad-hoc contacts client-side in ContactsTab (display only — not persisted).

// Ids are prefixed per list so a person appearing in both lists can't produce
// colliding React keys. Prefer mobile, then phone, for the displayed number (MSD-657)
// — `||` because an empty mobile must fall through to phone.
//
// MC1 pads its string fields with whitespace, so every field is trimmed here at the
// boundary instead of at each use. Padding is invisible on screen but compares
// unequal, and it has cost us twice: a padded mobile masked a real phone number
// (MSD-657), and a padded email stopped matching the same address coming from
// Outlook, so a participant was listed twice and never promoted to the top of the
// Contacts tab (MSD-642). Normalising once keeps every comparison downstream honest.
function mapParticipantToContact(p: Participant, index: number): Contact {
  const email = p.email?.trim() ?? ''
  const mobile = p.mobile?.trim()
  const phone = p.phone?.trim()
  const role = p.role?.trim() ?? ''
  return {
    // Keyed by email *and* role: MC1 sends one row per party-role pair, so someone holding
    // several roles on a claim arrived as several contacts sharing one id — React then had
    // duplicate keys in the Contacts list (MSD-642). Role keeps them distinct while the id
    // stays stable across refetches, which index alone would not.
    id: role ? `participant-${email || index}-${role}` : `participant-${email || index}`,
    name: p.fullName?.trim() ?? '',
    role,
    email,
    phone: mobile || phone || undefined,
  }
}

function mapAdhocToContact(a: AdhocContact, index: number): Contact {
  const email = a.email?.trim() ?? ''
  return {
    id: `adhoc-${email || index}`,
    name: a.name?.trim() ?? '',
    role: a.role?.trim() ?? '',
    email,
    isAdHoc: true,
  }
}

export async function getClaimParticipants(claimNumber: string, token: string): Promise<Contact[]> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  const response = await apiCall<ParticipantsResponse>(() => api.getClaimParticipantsRaw({ claimNumber }, { signal }))
  // Backend serializes an empty list as null — treat as no participants.
  return (response.participants ?? []).map((p, i) => mapParticipantToContact(p, i))
}

export async function getClaimAdhocContacts(claimNumber: string, token: string): Promise<Contact[]> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  const response = await apiCall<AdhocContactsResponse>(() => api.getClaimAdhocContactsRaw({ claimNumber }, { signal }))
  return (response.adhocContacts ?? []).map((a, i) => mapAdhocToContact(a, i))
}

// Persists an unmatched email recipient as an MC1 ad-hoc contact (MSD-256).
// The backend proc requires both name and email; the form enforces that. It also rejects a
// name+email pair already on the claim (Msg 105) — that comes back as a 400, so the proc's
// reason is unwrapped into an ApiValidationError instead of a generic bad-request error.
export async function addAdhocContact(
  claimNumber: string,
  request: AddAdhocContactRequest,
  token: string
): Promise<AddAdhocContactResponse> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  try {
    const response = await api.addClaimAdhocContactRaw({ claimNumber, addAdhocContactRequest: request }, { signal })
    return (await response.raw.json()) as AddAdhocContactResponse
  } catch (error) {
    if (error instanceof ResponseError && error.response.status === HTTP_STATUS.BAD_REQUEST) {
      const messages = await readValidationMessages(error.response)
      if (messages.length > 0) throw new ApiValidationError(messages)
    }
    return handleError(error)
  }
}

// Creates a File Note case note against a claim (MSD-236/237).
export async function createFileNoteCaseNote(
  claimNumber: string,
  request: CreateCaseNoteRequest,
  token: string
): Promise<CreateCaseNoteResponse> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  return apiCall<CreateCaseNoteResponse>(() =>
    api.createFileNoteCaseNoteRaw({ claimNumber, createCaseNoteRequest: request }, { signal })
  )
}

// Backend-controlled feature flags (e.g. EnableContactsTab) used to show or hide UI.
export async function getFeatureFlags(token: string): Promise<Record<string, boolean>> {
  const api = createSettingsApi(token)
  const signal = createTimeoutSignal()
  // The endpoint takes no parameters, so the single argument is initOverrides — unlike
  // the calls above, which pass (params, { signal }). Adding a `{}` first argument here
  // would silently drop the timeout signal without a type error.
  return apiCall<Record<string, boolean>>(() => api.getFeatureFlagsRaw({ signal }))
}

export async function downloadClaimDocument(id: string, token: string): Promise<Blob> {
  const api = createDocumentsApi(token)
  const signal = createTimeoutSignal()
  try {
    const response = await api.downloadDocumentRaw({ id }, { signal })
    return await response.raw.blob()
  } catch (error) {
    return handleError(error)
  }
}
